import { Button, Popconfirm, Select } from 'antd'
import React, {
  FunctionComponent,
  memo,
  useCallback,
  useMemo,
  useState,
} from 'react'

import {
  LeadState,
  leadStateLabels,
} from '../../../common/businessContactTypes'
import { BusinessContactTableItem } from '../../utils/useBusinessContactData'
import { useUpdateBusinessContact } from '../../utils/useUpdateBusinessContact'

export interface BusinessContactStateEditProps {
  record: BusinessContactTableItem
  onUpdate?: () => void
  children?: React.ReactNode
}

const stateOptions = Object.values(LeadState).map(value => ({
  label: leadStateLabels[value],
  value,
}))

const normalizeLeadState = (value: unknown): LeadState | undefined => {
  if (typeof value !== 'string') return undefined
  const lowered = value.toLowerCase()
  return (Object.values(LeadState) as string[]).includes(lowered)
    ? (lowered as LeadState)
    : undefined
}

export const BusinessContactStateEdit: FunctionComponent<BusinessContactStateEditProps> =
  memo(({ record, onUpdate, children }) => {
    const { id: recordId } = record
    const normalizedStateFromRecord = normalizeLeadState(record.state)
    const state = normalizedStateFromRecord ?? LeadState.NOT_CONTACTED
    const { updateState, isUpdating } = useUpdateBusinessContact()

    const [editedState, setEditedState] = useState<LeadState>(state)
    const [open, setOpen] = useState(false)

    const handleOpenChange = useCallback((newOpen: boolean) => {
      setOpen(newOpen)
    }, [])

    const handleConfirm = useCallback(async () => {
      const success = await updateState(recordId, editedState)
      if (success) {
        setOpen(false)
        onUpdate?.()
      }
    }, [editedState, onUpdate, recordId, updateState])

    const handleCancel = useCallback(() => {
      setEditedState(state)
      setOpen(false)
    }, [state])

    const buttonProps = useMemo(
      () => ({
        cancel: { size: 'middle' as const },
        ok: {
          loading: isUpdating,
          size: 'middle' as const,
        },
      }),
      [isUpdating]
    )

    return (
      <Popconfirm
        title="Změnit stav"
        placement="rightTop"
        autoAdjustOverflow
        description={
          <Select
            value={editedState}
            onChange={setEditedState}
            options={stateOptions}
            className="!w-40 !mb-2"
          />
        }
        open={open}
        icon={null}
        onOpenChange={handleOpenChange}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        okText="Uložit"
        cancelText="Zrušit"
        okButtonProps={buttonProps.ok}
        cancelButtonProps={buttonProps.cancel}
      >
        {children ? (
          <span className="inline-flex cursor-pointer">{children}</span>
        ) : (
          <Button size="small" type="link" loading={isUpdating}>
            Stav
          </Button>
        )}
      </Popconfirm>
    )
  })
